/**
 * /setup/notifications — notification preferences.
 *
 * Lives under Setup, visible only to users whose role has `notifications.view`
 * (see `setupNav` in `@/lib/navigation`) — but that's a NAV-VISIBILITY gate
 * only. The preferences themselves stay self-scoped: this page manages the
 * CALLER's own cadence, same as when it lived at /settings/notifications.
 * There's no tenant-wide control here, and no route-level permission check —
 * a user who opens this URL directly still only ever touches their own
 * preference row, so gating the page itself would add nothing.
 *
 * One "email frequency" control per digestable template (Immediate / Every 5
 * minutes / Every hour / Once a day) — e.g. rejections can be set to
 * Immediate while submissions/approvals stay on a daily digest. Saves on
 * select, per row. `immediate` sends per-event; the rest coalesce into a
 * digest. Only templates with `batchMode: 'window'` get a row — always-
 * immediate templates (reminders, etc.) aren't user-configurable here.
 *
 * Notifications themselves can't be turned off here — the digest emails are
 * mandatory; users only choose their cadence.
 */

import type { NotificationFrequency } from '@clockwise/shared';
import {
  ApiError,
  NOTIFICATION_FREQUENCIES,
  NOTIFICATION_FREQUENCY_LABELS,
} from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Section } from '@/components/custom/section';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { notificationsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export const Route = createFileRoute('/_authenticated/setup/notifications')({
  component: Preferences,
});

function Preferences() {
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.notifications.preferences(),
    queryFn: () => notificationsApi.listPreferences(),
  });

  // Only digestable templates get a frequency the user can tune — an
  // always-immediate template (e.g. a reminder) has no cadence to choose.
  const digestPreferences = data?.data.filter((p) => p.batchMode === 'window');

  return (
    <PageShell>
      <PageHeader
        title="Notifications"
        subtitle="Choose how often you get timesheet emails, per notification type."
      />

      <Section title="Email frequency" description="Applies to your own account only.">
        {isError ? (
          <p className="text-destructive text-sm">
            Couldn't load your notification preferences. Try reloading the page.
          </p>
        ) : isLoading || !digestPreferences ? (
          <ul className="divide-y rounded-md border">
            <RowSkeleton />
            <RowSkeleton />
            <RowSkeleton />
          </ul>
        ) : (
          <ul className="divide-y rounded-md border">
            {digestPreferences.map((p) => (
              <FrequencyRow
                key={p.templateName}
                templateName={p.templateName}
                label={p.label ?? p.templateName}
                description={p.description}
                frequency={p.frequency}
              />
            ))}
          </ul>
        )}
      </Section>
    </PageShell>
  );
}

function RowSkeleton() {
  return (
    <li className="flex items-center justify-between gap-4 px-4 py-3">
      <Skeleton className="h-9 flex-1" />
      <Skeleton className="h-9 w-44 shrink-0" />
    </li>
  );
}

/** One template's frequency control. Saves on select. */
function FrequencyRow({
  templateName,
  label,
  description,
  frequency,
}: {
  templateName: string;
  label: string;
  description?: string;
  frequency: NotificationFrequency;
}) {
  const queryClient = useQueryClient();

  const mutate = useMutation({
    mutationFn: (next: NotificationFrequency) =>
      notificationsApi.updatePreference(templateName, { frequency: next }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.preferences() });
      toast.success('Preference updated');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Failed to update preference');
    },
  });

  return (
    <li className="flex items-start justify-between gap-4 px-4 py-3">
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-medium">{label}</h3>
        {description ? <p className="mt-1 text-muted-foreground text-xs">{description}</p> : null}
      </div>

      <div className="shrink-0">
        <Select
          value={frequency}
          disabled={mutate.isPending}
          onValueChange={(v) => mutate.mutate(v as NotificationFrequency)}
        >
          <SelectTrigger className="w-44" aria-label={`${label} email frequency`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {NOTIFICATION_FREQUENCIES.map((f) => (
              <SelectItem key={f} value={f}>
                {NOTIFICATION_FREQUENCY_LABELS[f]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </li>
  );
}
